import { validateZipCode } from "../../../utlities/common.funcs";

export function formatPhoneNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function isValidAuthoredDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return false;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return false;
  }
  const [yearText, monthText, dayText] = raw.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1000 || year > 9999) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
  );
}

export function validateRequiredAndFormatDate(value, requiredMessage, invalidMessage) {
  if (!String(value || "").trim()) {
    return requiredMessage;
  }
  if (!isValidAuthoredDate(value)) {
    return invalidMessage;
  }
  return null;
}

/**
 * Canonical empty product-POC shape used to initialize the POC form in both the
 * standalone Product Submit flow and the ACR Editor. Keeping it here prevents the
 * field list from drifting between the two consumers.
 */
export function createEmptyProductPoc() {
  return {
    phoneNumber: "",
    email: "",
    street1: "",
    street2: "",
    city: "",
    state: null,
    zipCode: "",
  };
}

export function normalizeProductPocPayload(productPoc) {
  return {
    ...productPoc,
    phoneNumber: formatPhoneNumber(productPoc?.phoneNumber),
    email: String(productPoc?.email || "").trim(),
    street1: String(productPoc?.street1 || "").trim(),
    street2: String(productPoc?.street2 || "").trim(),
    city: String(productPoc?.city || "").trim(),
    state: productPoc?.state || null,
    zipCode: String(productPoc?.zipCode || "").trim(),
  };
}

export const US_PHONE_REGEX = /^\d{3}-\d{3}-\d{4}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PRODUCT_POC_PHONE_REQUIRED_MESSAGE = "Please include a POC phone number.";
export const PRODUCT_POC_PHONE_INVALID_MESSAGE = "Enter a valid US phone number (for example: 202-555-1212).";
export const PRODUCT_POC_EMAIL_REQUIRED_MESSAGE = "Please include a POC email address.";
export const PRODUCT_POC_EMAIL_INVALID_MESSAGE = "Enter a valid email address (for example: name@example.com).";

/**
 * Validate a product POC. Phone number and email are required (and format-checked);
 * ZIP is optional but format-checked when present. Returns an array of
 * `{ field, message }` failures using shared `productPoc.*` field keys so both the
 * standalone Product Submit flow and the ACR Editor can consume the same rules.
 */
export function validateProductPoc(productPoc) {
  const normalized = normalizeProductPocPayload(productPoc);
  const failures = [];
  if (!String(normalized.phoneNumber || "").trim()) {
    failures.push({ field: "productPoc.phoneNumber", message: PRODUCT_POC_PHONE_REQUIRED_MESSAGE });
  } else if (!US_PHONE_REGEX.test(normalized.phoneNumber)) {
    failures.push({ field: "productPoc.phoneNumber", message: PRODUCT_POC_PHONE_INVALID_MESSAGE });
  }
  if (!String(normalized.email || "").trim()) {
    failures.push({ field: "productPoc.email", message: PRODUCT_POC_EMAIL_REQUIRED_MESSAGE });
  } else if (!EMAIL_REGEX.test(normalized.email)) {
    failures.push({ field: "productPoc.email", message: PRODUCT_POC_EMAIL_INVALID_MESSAGE });
  }
  const zipMessage = validateZipCode(normalized.zipCode);
  if (zipMessage) {
    failures.push({ field: "productPoc.zipCode", message: zipMessage });
  }
  return failures;
}
